import {
  WorkSpace,
  WorkSpaceAccessToken,
  WorkSpaceRefreshToken,
  WorkSpaceSettings,
  WorkSpaceUserPermissions,
  WorkSpaceUserRoles,
} from "@/core/entities/workspace";
import { IAuthRepository } from "@/core/repositories/auth";
import { IWorkSpaceRepository } from "@/core/repositories/workspace";

export class WorkSpaceRepository implements IWorkSpaceRepository {
  constructor(private readonly authRepository: IAuthRepository) {}

  async getWorkspace(): Promise<WorkSpace | null> {
    const userCurrentBrand = await this.authRepository.getUserCurrentBrandFromStorage();
    const userBrands = await this.authRepository.getUserBrandsFromStorage();
    const userBrandFromUserBrands = userBrands?.find(brand => brand.brandKey === userCurrentBrand?.brandKey);

    if (!userCurrentBrand || !userBrands || userBrands.length === 0 || !userBrandFromUserBrands) {
      return null;
    }

    return new WorkSpace({
      workspaceKey: userCurrentBrand.brandKey,
      workspaceName: userBrandFromUserBrands.brandName,
      workspaceLogo: userBrandFromUserBrands.brandLogo,
      organizationKey: userBrandFromUserBrands.organizationKey,
      organizationName: userBrandFromUserBrands.organizationName,
      organizationLogo: userBrandFromUserBrands.organizationLogo,
    });
  }

  async getWorkspaceAccessToken(): Promise<WorkSpaceAccessToken | null> {
    const userCurrentBrand = await this.authRepository.getUserCurrentBrandFromStorage();
    if (!userCurrentBrand?.brandAccessToken) {
      return null;
    }
    return new WorkSpaceAccessToken({
      accessToken: userCurrentBrand.brandAccessToken,
    });
  }

  async getWorkspaceRefreshToken(): Promise<WorkSpaceRefreshToken | null> {
    const userCurrentBrand = await this.authRepository.getUserCurrentBrandFromStorage();
    if (!userCurrentBrand?.brandRefreshToken) {
      return null;
    }
    return new WorkSpaceRefreshToken({
      refreshToken: userCurrentBrand.brandRefreshToken,
    });
  }

  async getWorkspaceSettings(): Promise<WorkSpaceSettings | null> {
    const userCurrentBrand = await this.authRepository.getUserCurrentBrandFromStorage();
    const brandSettings = userCurrentBrand?.settings;

    return new WorkSpaceSettings({
      displayDateFormat: brandSettings?.displayDateFormat,
      displayDateFormatWithTime: brandSettings?.displayDateTimeFormat,
      displayTimeFormat: brandSettings?.displayTimeFormat,
      displayNumberFormat: brandSettings?.displayNumberFormat,
      defaultCheckDateRange: brandSettings?.defaultCheckDateRange,
    });
  }

  async getWorkspaceUserRoles(): Promise<WorkSpaceUserRoles | null> {
    const userCurrentBrand = await this.authRepository.getUserCurrentBrandFromStorage();
    if (!userCurrentBrand?.userRoles || userCurrentBrand.userRoles.length === 0) {
      return null;
    }
    return new WorkSpaceUserRoles({
      roles: userCurrentBrand.userRoles,
      roleNames: userCurrentBrand.userRoleDisplayNames,
    });
  }

  async getWorkspaceUserPermissions(): Promise<WorkSpaceUserPermissions | null> {
    const userCurrentBrand = await this.authRepository.getUserCurrentBrandFromStorage();
    if (!userCurrentBrand?.userPermissions || userCurrentBrand.userPermissions.length === 0) {
      return null;
    }
    return new WorkSpaceUserPermissions({
      permissions: userCurrentBrand.userPermissions,
    });
  }
}
